import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { listOrders, moveOrder, removeOrder, type Order, type OrderStatus } from './api';

/**
 * Orders (Phase 123).
 *
 * The screen offers exactly the moves the server will accept — `nextStatuses` comes from the same
 * transition table the API checks against, so a button that appears is a button that works. A
 * dropdown of every status would be a screen inviting people to try things and be refused, and
 * refusals are how people learn a product is fighting them.
 */

const TONE: Record<OrderStatus, BadgeTone> = {
  DRAFT: 'neutral',
  CONFIRMED: 'info',
  IN_PRODUCTION: 'info',
  READY: 'warning',
  SHIPPED: 'warning',
  DELIVERED: 'positive',
  CANCELLED: 'neutral',
  RETURNED: 'danger',
};

const FILTERS: (OrderStatus | 'ALL')[] = [
  'ALL',
  'DRAFT',
  'CONFIRMED',
  'IN_PRODUCTION',
  'READY',
  'SHIPPED',
  'DELIVERED',
];

export function OrdersPage(): React.JSX.Element {
  const client = useQueryClient();
  const [filter, setFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [error, setError] = useState<string | null>(null);

  const orders = useQuery({
    queryKey: ['orders', filter],
    queryFn: () => listOrders(filter === 'ALL' ? undefined : filter),
  });

  const move = useMutation({
    mutationFn: (input: { id: string; status: OrderStatus; reason?: string }) =>
      moveOrder(input.id, { status: input.status, reason: input.reason ?? null }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const drop = useMutation({
    mutationFn: (id: string) => removeOrder(id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<Order>[] = [
    {
      key: 'number',
      header: t('orders.number'),
      render: (order) => (
        <span className="cell-stack">
          <span className="strong">#{order.number}</span>
          <span className="muted small">{formatDate(order.createdAt)}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('orders.status'),
      render: (order) => (
        <span className="cell-stack">
          <Badge tone={TONE[order.status]}>{t(`orders.state.${order.status}`)}</Badge>
          {order.cancelledReason && <span className="muted small">{order.cancelledReason}</span>}
        </span>
      ),
    },
    {
      key: 'items',
      header: t('orders.items'),
      secondary: true,
      render: (order) => (
        <span className="cell-stack">
          <span>{t('orders.itemCount').replace('{n}', String(order.items.length))}</span>
          {order.expectedDate && (
            <span className="muted small">
              {t('orders.expected').replace('{date}', formatDate(order.expectedDate))}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('orders.total'),
      align: 'end',
      render: (order) => formatMoney(order.totalCents, order.currency),
    },
    {
      key: 'moves',
      header: '',
      render: (order) => (
        <span className="cell-inline">
          {/*
           * Only the moves the server will accept. A cancellation asks why first, because the
           * API refuses one without a reason — and a button that produces an error somebody has
           * to read is a button that should have asked.
           */}
          {order.nextStatuses.map((status) => (
            <button
              key={status}
              type="button"
              className="button button--quiet button--compact"
              disabled={move.isPending}
              onClick={() => {
                if (status === 'CANCELLED') {
                  const reason = window.prompt(t('orders.whyCancel'));

                  if (!reason?.trim()) return;

                  move.mutate({ id: order.id, status, reason });

                  return;
                }

                move.mutate({ id: order.id, status });
              }}
            >
              {t(`orders.move.${status}`)}
            </button>
          ))}

          {order.status === 'DRAFT' && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => drop.mutate(order.id)}
            >
              {t('orders.delete')}
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('orders.title')}
        description={t('orders.description')}
        actions={
          <select
            className="select select--compact"
            value={filter}
            onChange={(event) => setFilter(event.target.value as OrderStatus | 'ALL')}
            aria-label={t('orders.status')}
          >
            {FILTERS.map((value) => (
              <option key={value} value={value}>
                {value === 'ALL' ? t('orders.all') : t(`orders.state.${value}`)}
              </option>
            ))}
          </select>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel>
        <DataTable
          tableKey="orders"
          rows={orders.data?.orders}
          columns={columns}
          rowKey={(order) => order.id}
          isLoading={orders.isLoading}
          error={orders.error}
          onRetry={() => void orders.refetch()}
          empty={{ title: t('orders.emptyTitle'), description: t('orders.emptyBody') }}
        />
      </Panel>
    </div>
  );
}
